import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Plus, Edit2, Trash2, Save, X, DollarSign, Package, Star, Upload, Image, GripVertical, Eye, EyeOff, ArrowUp, ArrowDown, Loader2, Tag } from 'lucide-react';
import { api } from '../services/api';

export const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: 'books',
    image: '',
    inStock: true,
    featured: false,
    badge: '',
    clubTags: []
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Sample products data
  const sampleProducts = [
    {
      id: 'curi-kitten-book',
      name: 'Curi The Kitten - Children\'s Book',
      description: 'A heartwarming tale about kindness and adventure. Perfect for teaching children about compassion.',
      price: 12.99,
      category: 'books',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400',
      inStock: true,
      featured: true,
      badge: '',
      sales: 127,
      revenue: 1649.73
    },
    {
      id: 'premium-travel-tags',
      name: 'Premium QR Travel Tags (Pack of 10)',
      description: 'Waterproof, durable QR code stickers perfect for tracking items on journeys.',
      price: 8.99,
      category: 'tags',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400',
      inStock: true,
      featured: true,
      badge: '',
      sales: 89,
      revenue: 800.11
    },
    {
      id: 'starter-kit',
      name: 'TrackMe Starter Kit',
      description: 'Everything you need to start spreading kindness! Book + 20 tags + guide.',
      price: 24.99,
      category: 'kits',
      image: 'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=400',
      inStock: true,
      featured: true,
      badge: 'Best Value',
      sales: 156,
      revenue: 3898.44
    }
  ];

  const categories = [
    { value: 'books', label: 'Books' },
    { value: 'tags', label: 'Travel Tags' },
    { value: 'kits', label: 'Starter Kits' },
    { value: 'education', label: 'Education' },
    { value: 'merchandise', label: 'Merchandise' }
  ];

  const availableClubTags = [
    { value: 'jeepduck', label: 'JeepDuck.club', color: 'bg-amber-100 text-amber-800 border-amber-300' },
    { value: 'travelrock', label: 'TravelRock.club', color: 'bg-pink-100 text-pink-800 border-pink-300' },
    { value: 'curi', label: 'CuriTheKitten.com', color: 'bg-orange-100 text-orange-800 border-orange-300' }
  ];

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await api.getAdminProducts();
      if (response.products) {
        setProducts(response.products);
      } else {
        // Fallback to sample data if no products in database
        setProducts(sampleProducts);
      }
    } catch (error) {
      console.error('Failed to load products:', error);
      // Fallback to sample data on error
      setProducts(sampleProducts);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (file) => {
    try {
      console.log('📤 Uploading file:', file.name, file.size, 'bytes');
      const response = await api.uploadProductImage(file);
      if (response.success) {
        return response.imageUrl;
      } else {
        throw new Error(response.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Image upload failed:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('🔄 handleSubmit called');
    console.log('📦 formData:', JSON.stringify(formData));
    console.log('📁 selectedFile:', selectedFile ? selectedFile.name : 'none');
    console.log('✏️ editingProduct:', editingProduct ? editingProduct.id : 'new');
    
    setSavingProduct(true);
    setSaveMessage({ type: '', text: '' });
    
    try {
      let imageUrl = formData.image || '';
      
      // Upload image if file is selected
      if (selectedFile) {
        console.log('📤 Uploading file:', selectedFile.name, selectedFile.size, 'bytes');
        setSaveMessage({ type: 'info', text: 'Uploading image...' });
        try {
          imageUrl = await uploadImage(selectedFile);
          console.log('✅ Upload successful, imageUrl:', imageUrl);
        } catch (uploadError) {
          console.error('❌ Upload error:', uploadError);
          setSaveMessage({ type: 'error', text: `Image upload failed: ${uploadError.message}` });
          setSavingProduct(false);
          return;
        }
      }
      
      const productData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price) || 0,
        category: formData.category,
        image: imageUrl,
        inStock: formData.inStock,
        featured: formData.featured,
        badge: formData.badge || '',
        clubTags: formData.clubTags || []
      };
      
      console.log('📦 productData to save:', JSON.stringify(productData));
      setSaveMessage({ type: 'info', text: 'Saving product...' });
      
      if (editingProduct) {
        // Update existing product
        const productId = editingProduct.id || editingProduct.uid;
        console.log('✏️ Updating product:', productId);
        if (!productId) {
          setSaveMessage({ type: 'error', text: 'Error: Product ID not found' });
          setSavingProduct(false);
          return;
        }
        const result = await api.updateProduct(productId, productData);
        console.log('✅ Update result:', result);
        setEditingProduct(null);
      } else {
        // Add new product
        console.log('➕ Creating new product');
        const result = await api.createProduct(productData);
        console.log('✅ Create result:', result);
      }
      
      // Reload products from server
      console.log('🔄 Reloading products...');
      await loadProducts();
      console.log('✅ Products reloaded');
      
      // Reset form
      setFormData({
        name: '',
        description: '',
        price: '',
        category: 'books',
        image: '',
        inStock: true,
        featured: false,
        badge: '',
        clubTags: []
      });
      setSelectedFile(null);
      setImagePreview(null);
      setShowAddForm(false);
      setSaveMessage({ type: 'success', text: 'Product saved successfully!' });
      
      // Clear success message after 3 seconds
      setTimeout(() => setSaveMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('❌ Save product error:', error);
      setSaveMessage({ type: 'error', text: `Failed to save: ${error.message || 'Please try again.'}` });
    } finally {
      setSavingProduct(false);
    }
  };

  const handleEdit = (product) => {
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      image: product.image,
      inStock: product.inStock,
      featured: product.featured,
      badge: product.badge || '',
      clubTags: product.clubTags || []
    });
    setEditingProduct(product);
    setShowAddForm(true);
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await api.deleteProduct(productId);
        await loadProducts(); // Reload products from server
      } catch (error) {
        console.error('Failed to delete product:', error);
        alert('Failed to delete product. Please try again.');
      }
    }
  };

  const toggleProductStatus = async (productId, field) => {
    try {
      const product = products.find(p => p.id === productId);
      if (!product) return;
      
      const updatedProduct = { ...product, [field]: !product[field] };
      await api.updateProduct(productId, updatedProduct);
      await loadProducts(); // Reload products from server
    } catch (error) {
      console.error('Failed to update product status:', error);
      alert('Failed to update product. Please try again.');
    }
  };

  const getTotalRevenue = () => {
    return products.reduce((total, product) => total + (product.revenue || 0), 0);
  };

  const getTotalSales = () => {
    return products.reduce((total, product) => total + (product.sales || 0), 0);
  };

  // Move product up in order
  const moveProductUp = (index) => {
    if (index === 0) return;
    const newProducts = [...products];
    [newProducts[index - 1], newProducts[index]] = [newProducts[index], newProducts[index - 1]];
    setProducts(newProducts);
  };

  // Move product down in order
  const moveProductDown = (index) => {
    if (index === products.length - 1) return;
    const newProducts = [...products];
    [newProducts[index], newProducts[index + 1]] = [newProducts[index + 1], newProducts[index]];
    setProducts(newProducts);
  };

  // Save the new order to backend
  const saveProductOrder = async () => {
    try {
      setSavingOrder(true);
      const productOrder = products.map((product, index) => ({
        id: product.id || product.uid,
        sortOrder: index + 1
      }));
      
      await api.reorderProducts(productOrder);
      alert('Product order saved successfully!');
    } catch (error) {
      console.error('Failed to save product order:', error);
      alert('Failed to save product order. Please try again.');
    } finally {
      setSavingOrder(false);
    }
  };

  // Toggle product active/inactive status
  const toggleProductActive = async (productId) => {
    try {
      const result = await api.toggleProductActive(productId);
      if (result.success) {
        // Update local state
        setProducts(products.map(p => 
          (p.id === productId || p.uid === productId) 
            ? { ...p, isActive: result.isActive } 
            : p
        ));
      }
    } catch (error) {
      console.error('Failed to toggle product status:', error);
      alert('Failed to update product status. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Package className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Total Products</p>
                <p className="text-2xl font-bold">{products.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Star className="w-8 h-8 text-yellow-600 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Total Sales</p>
                <p className="text-2xl font-bold">{getTotalSales()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <DollarSign className="w-8 h-8 text-green-600 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold">${getTotalRevenue().toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add Product Button */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Product Management</h2>
        <Button 
          onClick={() => setShowAddForm(true)}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New Product
        </Button>
      </div>

      {/* Add/Edit Product Form */}
      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editingProduct ? 'Edit Product' : 'Add New Product'}
              <Button 
                variant="ghost" 
                size="sm" 
                className="float-right"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingProduct(null);
                  setFormData({
                    name: '',
                    description: '',
                    price: '',
                    category: 'books',
                    image: '',
                    inStock: true,
                    featured: false,
                    badge: '',
                    clubTags: []
                  });
                  setSelectedFile(null);
                  setImagePreview(null);
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., Curi The Kitten Book"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price *</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', e.target.value)}
                  placeholder="12.99"
                  required
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  rows="3"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe your product..."
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Badge (Optional)</label>
                <Input
                  value={formData.badge}
                  onChange={(e) => handleInputChange('badge', e.target.value)}
                  placeholder="e.g., Best Value, New"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Tag className="w-3.5 h-3.5 inline mr-1" />
                  Club Tags (show on club pages)
                </label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {availableClubTags.map(tag => {
                    const isSelected = (formData.clubTags || []).includes(tag.value);
                    return (
                      <button
                        key={tag.value}
                        type="button"
                        onClick={() => {
                          const current = formData.clubTags || [];
                          const updated = isSelected
                            ? current.filter(t => t !== tag.value)
                            : [...current, tag.value];
                          handleInputChange('clubTags', updated);
                        }}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                          isSelected
                            ? tag.color + ' ring-2 ring-offset-1 ring-current'
                            : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                        }`}
                        data-testid={`club-tag-${tag.value}`}
                      >
                        {isSelected ? '✓ ' : ''}{tag.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-1">Select clubs where this product should appear</p>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Image</label>
                
                {/* Image Upload Section */}
                <div className="space-y-4">
                  {/* File Upload */}
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                        id="image-upload"
                      />
                      <label
                        htmlFor="image-upload"
                        className="flex items-center justify-center px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-emerald-400 transition-colors"
                      >
                        <Upload className="w-5 h-5 mr-2 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {selectedFile ? selectedFile.name : 'Click to upload image'}
                        </span>
                      </label>
                    </div>
                    
                    {/* OR divider */}
                    <div className="flex items-center">
                      <div className="border-t border-gray-300 flex-1"></div>
                      <span className="px-3 text-sm text-gray-500">OR</span>
                      <div className="border-t border-gray-300 flex-1"></div>
                    </div>
                    
                    {/* URL Input */}
                    <div className="flex-1">
                      <Input
                        value={formData.image}
                        onChange={(e) => handleInputChange('image', e.target.value)}
                        placeholder="Paste image URL here"
                        className={selectedFile ? 'bg-gray-50' : ''}
                        disabled={selectedFile !== null}
                      />
                    </div>
                  </div>
                  
                  {/* Image Preview */}
                  {(imagePreview || formData.image) && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Preview:</label>
                      <div className="relative inline-block">
                        <img
                          src={imagePreview || formData.image}
                          alt="Product preview"
                          className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="absolute -top-2 -right-2 h-6 w-6 p-0 bg-white border border-gray-300"
                          onClick={() => {
                            setImagePreview(null);
                            setSelectedFile(null);
                            handleInputChange('image', '');
                            document.getElementById('image-upload').value = '';
                          }}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-500">
                    💡 Upload an image file or paste an image URL. Recommended size: 400x400px or larger.
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.inStock}
                    onChange={(e) => handleInputChange('inStock', e.target.checked)}
                    className="mr-2"
                  />
                  In Stock
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => handleInputChange('featured', e.target.checked)}
                    className="mr-2"
                  />
                  Featured Product
                </label>
              </div>
              
              {/* Save Message Display */}
              {saveMessage.text && (
                <div className={`md:col-span-2 p-3 rounded-lg flex items-center gap-2 ${
                  saveMessage.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
                  saveMessage.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
                  'bg-blue-50 text-blue-800 border border-blue-200'
                }`}>
                  {saveMessage.type === 'info' && <Loader2 className="w-4 h-4 animate-spin" />}
                  {saveMessage.text}
                </div>
              )}
              
              <div className="md:col-span-2 flex gap-2">
                <Button 
                  type="submit" 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={savingProduct}
                >
                  {savingProduct ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {editingProduct ? 'Update Product' : 'Add Product'}
                    </>
                  )}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  disabled={savingProduct}
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingProduct(null);
                    setSelectedFile(null);
                    setImagePreview(null);
                    setSaveMessage({ type: '', text: '' });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Products List Header with Save Order Button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-700">Products ({products.length})</h3>
        <Button
          onClick={saveProductOrder}
          disabled={savingOrder}
          variant="outline"
          className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
        >
          {savingOrder ? (
            <>Saving...</>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Order
            </>
          )}
        </Button>
      </div>

      {/* Products List */}
      <div className="grid gap-4">
        {products.map((product, index) => (
          <Card key={product.id} className={`${product.isActive === false ? 'opacity-60 bg-gray-50' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                {/* Reorder Controls */}
                <div className="flex flex-col gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveProductUp(index)}
                    disabled={index === 0}
                    className="h-6 w-6 p-0"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </Button>
                  <div className="text-xs text-center text-gray-400 font-medium">{index + 1}</div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveProductDown(index)}
                    disabled={index === products.length - 1}
                    className="h-6 w-6 p-0"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </Button>
                </div>

                <img 
                  src={product.image} 
                  alt={product.name}
                  className="w-16 h-16 object-cover rounded-lg"
                />
                
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-lg">{product.name}</h3>
                    {product.badge && (
                      <Badge className="bg-pink-600 text-white text-xs">{product.badge}</Badge>
                    )}
                    {product.featured && (
                      <Badge className="bg-yellow-600 text-white text-xs">Featured</Badge>
                    )}
                    <Badge variant={product.inStock ? "outline" : "destructive"}>
                      {product.inStock ? "In Stock" : "Out of Stock"}
                    </Badge>
                    {product.isActive === false && (
                      <Badge variant="secondary" className="bg-gray-200 text-gray-600">
                        Inactive
                      </Badge>
                    )}
                    {(product.clubTags || []).map(tag => {
                      const clubInfo = availableClubTags.find(c => c.value === tag);
                      return clubInfo ? (
                        <Badge key={tag} className={`${clubInfo.color} text-xs`}>
                          {clubInfo.label}
                        </Badge>
                      ) : null;
                    })}
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-2">{product.description}</p>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="font-medium text-emerald-600">${product.price}</span>
                    <span>Category: {categories.find(c => c.value === product.category)?.label}</span>
                    <span>Sales: {product.sales || 0}</span>
                    <span>Revenue: ${(product.revenue || 0).toFixed(2)}</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Active/Inactive Toggle */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleProductActive(product.id || product.uid)}
                    className={product.isActive === false ? "text-gray-500" : "text-green-600 bg-green-50 border-green-300"}
                    title={product.isActive === false ? "Click to activate" : "Click to deactivate"}
                  >
                    {product.isActive === false ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleProductStatus(product.id, 'featured')}
                    className={product.featured ? "bg-yellow-50 border-yellow-300" : ""}
                  >
                    <Star className="w-4 h-4" />
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(product)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(product.id)}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ProductManagement;